import { useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@volca/ui/components/ui/dialog";
import { Input } from "@volca/ui/components/ui/input";
import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { Stack } from "@volca/ui/components/ui/stack";
import { cn } from "@volca/ui/lib/utils";
import { useReportCurrency } from "@/profitability/currency";
import { SETTINGS_CONTROLS } from "@/profitability/settings/control-classes";
import { SettingField } from "@/profitability/settings/setting-field";
import type { TemplateSetting } from "@/profitability/settings/types";
import { effectiveValue, formatSetting } from "@/profitability/settings/values";
import { SettingsGroup, SettingsRow } from "./settings-group";
import type { TemplateSettingsState } from "./use-settings-state";

/** A draft stays in the form until Save succeeds. */
export const EditSetting = ({
  setting,
  open,
  onOpenChange,
  onSave,
}: {
  setting: TemplateSetting;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (value: TemplateSetting["value"]) => Promise<void>;
}) => {
  const [pending, setPending] = useState(false);
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!pending) onOpenChange(next);
      }}
    >
      <DialogContent
        className={cn(
          "max-h-[calc(100dvh-2rem)] max-w-md overflow-y-auto overscroll-contain",
          SETTINGS_CONTROLS,
        )}
      >
        <DialogHeader>
          <DialogTitle>{setting.label}</DialogTitle>
          <DialogDescription>
            Changes apply to future reports. Rerun an earlier period to update
            it.
          </DialogDescription>
        </DialogHeader>
        <SettingField
          setting={setting}
          editable
          onSave={async (value) => {
            setPending(true);
            try {
              await onSave(value);
            } finally {
              setPending(false);
            }
          }}
          onCancel={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
};

/**
 * The numbers a template uses that its files don't hold: a burden rate, a
 * margin target, a target per business unit. Each shows what it is set to
 * now, and whether that is the template's default, with an edit beside it.
 */
export const RatesGroup = ({ state }: { state: TemplateSettingsState }) => {
  const currency = useReportCurrency();
  const [editing, setEditing] = useState<string | null>(null);
  const { settings, editable, save } = state;
  const [search, setSearch] = useState("");
  const declared = settings?.declared ?? [];
  const shown = declared.filter((setting) =>
    `${setting.label} ${setting.description ?? ""}`
      .toLowerCase()
      .includes(search.trim().toLowerCase()),
  );
  const open = declared.find((setting) => setting.key === editing);
  return (
    <SettingsGroup
      title="Report values"
      description="Rates, targets and thresholds used in calculations and review rules."
      footer={
        declared.length > 0
          ? "Changes apply to future reports. Rerun an earlier period to update it."
          : undefined
      }
    >
      {declared.length > 0 && (
        <Stack className="p-4">
          <Input
            aria-label="Find a calculation or threshold"
            placeholder="Find a rate, target or threshold…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </Stack>
      )}
      {!settings ? (
        <div className="p-4">
          <Skeleton
            role="status"
            aria-label="Loading report values"
            className="h-10 w-full"
          />
        </div>
      ) : declared.length === 0 ? (
        <SettingsRow
          label="No configurable values"
          hint="This version uses values from its source files. Edit the agent to add a configurable value."
        />
      ) : (
        shown.map((setting) => (
          <SettingsRow
            key={setting.key}
            label={setting.label}
            value={
              <Stack direction="row" gap={2} align="center" wrap>
                <span className="tabular-nums">
                  {formatSetting(setting, effectiveValue(setting), currency)}
                </span>
              </Stack>
            }
            hint={setting.description}
            action={
              editable && (
                <Button
                  variant="outline"
                  size="sm"
                  aria-label={`Edit ${setting.label}`}
                  onClick={() => setEditing(setting.key)}
                >
                  Edit
                </Button>
              )
            }
          />
        ))
      )}
      {settings && declared.length > 0 && shown.length === 0 && (
        <SettingsRow
          label="No matching values"
          action={
            <Button variant="ghost" size="sm" onClick={() => setSearch("")}>
              Clear search
            </Button>
          }
        />
      )}
      {open && (
        <EditSetting
          setting={open}
          open
          onOpenChange={(next) => !next && setEditing(null)}
          onSave={(value) => save({ values: { [open.key]: value } })}
        />
      )}
    </SettingsGroup>
  );
};
